import { PARTICIPANT_TYPE } from '@/configs'
import dayjs from '@/lib/dayjs'
import { DayjsProperty } from '@/lib/jackson'
import { Dayjs } from 'dayjs'
import { JsonProperty, Serializable } from 'ts-jackson'
import { Member } from '@/services/member/model'
import { Fromable } from '@/services/model'

export enum SWIMMING_SCHEDULE_LIST_STATUS {
  ALL = 'all',
  ACTIVE = 'active',
  UPCOMING = 'upcoming',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export enum SWIMMING_SCHEDULE_TYPE {
  NOW = 'open-now',
  UPCOMING = 'upcoming',
}

export enum SWIMMING_EXCHANGE {
  FREE = 'free-swimming',
  CREDIT = 'credit-exchange',
}

export type SwimmingBookingPayload = {
  userId: string | number
  scheduleId: string | number
  userMembershipId: string | number
}

export type SwimmingScheduleFilter = {
  startTime?: string
  endTime?: string
  status?: string
  branchId?: string | number
  trainerId?: string | number
  query?: string
  isPrivate?: boolean
  memberType?: string
}

export type SwimmingChangeMemberPayload = {
  userId: number
  userMembershipId?: number | null
}

export type SwimmingScheduleCreatePayload = {
  startTime: string
  endTime: string
  cancelBefore?: number
  location: string
  capacity: number
  description: string
  level: string
  trainerIds: number[]
  publishedAt?: string | null
  price?: number
}

export type SwimmingScheduleCreateWithBookingPayload = SwimmingScheduleCreatePayload & {
  memberIds: number[]
  isRepeat: boolean
  isPrivate?: boolean
  singleSession?: {
    appointmentDate: string
    startTime: string
    endTime: string
    cancelBefore?: number
  }
  recurringSchedule?: {
    startDate: string
    endDate: string
    singleTimeDurations: {
      dayOfWeekIndex: number
      startTime: string
      endTime: string
    }[]
    cancelBefore?: number
  }
  userMembershipId?: number
  memberType?: PARTICIPANT_TYPE
}

export type SwimmingReschedulePayload = {
  scheduleId: string | number
}

@Serializable()
export class SwimmingTrainer extends Fromable {
  @JsonProperty()
  id!: number

  @JsonProperty()
  code!: string

  @JsonProperty()
  firstName!: string

  @JsonProperty()
  lastName!: string

  @JsonProperty()
  nickname!: string

  @JsonProperty()
  gender!: string

  @JsonProperty()
  email!: string

  @JsonProperty()
  phone!: string
}

@Serializable()
export class SwimmingTrainerWithAvailableTime extends Fromable {
  @JsonProperty()
  startTime!: Dayjs

  @JsonProperty()
  endTime!: Dayjs

  @JsonProperty({ type: SwimmingTrainer })
  trainer!: SwimmingTrainer
}

@Serializable()
export class SwimmingSingleSession extends Fromable {
  @JsonProperty()
  appointmentDate!: Dayjs

  @JsonProperty()
  startTime!: Dayjs

  @JsonProperty()
  endTime!: Dayjs
}

@Serializable()
export class SwimmingTimeDuration extends Fromable {
  @JsonProperty()
  dayOfWeekIndex!: number

  @JsonProperty()
  startTime!: Dayjs

  @JsonProperty()
  endTime!: Dayjs

  @JsonProperty()
  trainerId?: number
}

@Serializable()
export class SwimmingRecurringSchedule extends Fromable {
  @JsonProperty()
  startDate!: Dayjs

  @JsonProperty()
  endDate!: Dayjs

  @JsonProperty({ elementType: SwimmingTimeDuration })
  singleTimeDurations!: SwimmingTimeDuration[]
}

@Serializable()
export class SwimmingBooking extends Fromable {
  @JsonProperty()
  id!: number

  @JsonProperty()
  userId!: number

  @JsonProperty()
  scheduleId!: number

  @JsonProperty()
  status!: string

  @DayjsProperty()
  bookedAt!: Dayjs

  @DayjsProperty()
  cancelledAt?: Dayjs | null

  @JsonProperty({ type: Member })
  user!: Member

  @JsonProperty()
  userMembershipId?: number | null
}

@Serializable()
export class SwimmingBookingItem extends Fromable {
  @JsonProperty()
  memberId!: number

  @JsonProperty()
  userMembershipId?: number
}

@Serializable()
export class SwimmingSchedule extends Fromable {
  @JsonProperty()
  id!: number

  @JsonProperty()
  groupId?: string

  @JsonProperty()
  branchId!: number

  @JsonProperty()
  userMembershipId?: number

  @DayjsProperty()
  startTime!: Dayjs

  @DayjsProperty()
  endTime!: Dayjs

  @DayjsProperty()
  cancelledAt?: Dayjs

  @JsonProperty()
  cancelBefore!: number

  @DayjsProperty()
  publishedAt?: Dayjs | null

  @JsonProperty()
  price!: number

  @JsonProperty()
  durationMinutes!: number

  @JsonProperty()
  capacity!: number

  @JsonProperty()
  isPrivate!: boolean

  @JsonProperty()
  location!: string

  @JsonProperty()
  description!: string

  @JsonProperty()
  level!: string

  @JsonProperty({ elementType: SwimmingTrainer })
  trainers?: SwimmingTrainer[]

  @JsonProperty({ elementType: Member })
  members?: Member[]

  @JsonProperty({ elementType: SwimmingBookingItem })
  bookings?: SwimmingBookingItem[]

  @JsonProperty()
  memberType?: PARTICIPANT_TYPE

  @JsonProperty({ type: SwimmingRecurringSchedule })
  recurringSchedule?: SwimmingRecurringSchedule

  @JsonProperty({ type: SwimmingSingleSession })
  singleSession?: SwimmingSingleSession

  @JsonProperty({ elementType: SwimmingBooking })
  booking?: SwimmingBooking[]

  @JsonProperty('bookingCount')
  bookingCount?: number

  get attendanceCount() {
    return this.booking?.length ?? 0
  }

  set attendanceCount(_: number) {
    // ignore incoming writes; attendanceCount is derived from bookings
  }

  public serializeToCreate() {
    return {
      startTime: this.startTime.toISOString(),
      endTime: this.endTime.toISOString(),
      cancelBefore: this.cancelBefore ?? 60,
      location: this.location,
      capacity: this.capacity,
      description: this.description,
      level: this.level,
      trainerIds: this.trainers?.map((trainer) => trainer.id) ?? [],
      publishedAt: this.publishedAt?.toISOString() ?? null,
      price: this.price ?? 0,
    }
  }

  public serializeToCreateWithBooking() {
    let session = {}

    if (this.singleSession && Object.keys(this.singleSession).length > 0) {
      session = {
        singleSession: {
          appointmentDate: this.singleSession.appointmentDate.toISOString(),
          startTime: this.singleSession.startTime.format('HH:mm'),
          endTime: this.singleSession.endTime.format('HH:mm'),
          cancelBefore: this.cancelBefore,
        },
      }
    }

    if (this.recurringSchedule && Object.keys(this.recurringSchedule).length > 0) {
      session = {
        recurringSchedule: {
          startDate: this.recurringSchedule.startDate.toISOString(),
          endDate: this.recurringSchedule.endDate.toISOString(),
          singleTimeDurations: this.recurringSchedule.singleTimeDurations.map((duration) => ({
            dayOfWeekIndex: duration.dayOfWeekIndex,
            startTime: duration.startTime.format('HH:mm'),
            endTime: duration.endTime.format('HH:mm'),
            ...(typeof duration.trainerId === 'number' ? { trainerId: duration.trainerId } : {}),
          })),
          cancelBefore: this.cancelBefore,
        },
      }
    }

    return {
      location: this.location,
      capacity: this.capacity,
      bookings:
        this.bookings?.map((b) => ({
          memberId: b.memberId,
          userMembershipId: b.userMembershipId,
        })) ?? [],
      trainerIds: this.trainers?.map((trainer) => trainer.id) ?? [],
      price: this.price,
      level: this.level,
      memberType: this.memberType,
      isRepeat: !!this.recurringSchedule,
      isPrivate: this.isPrivate || false,
      ...session,
    }
  }

  public getTimeStatus(): SWIMMING_SCHEDULE_LIST_STATUS {
    const now = dayjs()
    if (this.cancelledAt) {
      return SWIMMING_SCHEDULE_LIST_STATUS.CANCELLED
    }

    if (
      dayjs(this.startTime).isAfter(now) &&
      this.publishedAt &&
      dayjs(this.publishedAt).isAfter(now)
    ) {
      return SWIMMING_SCHEDULE_LIST_STATUS.UPCOMING
    }
    if (dayjs(this.endTime).isBefore(now)) {
      return SWIMMING_SCHEDULE_LIST_STATUS.COMPLETED
    }

    return SWIMMING_SCHEDULE_LIST_STATUS.ACTIVE
  }

  public getStatus() {
    const capacityAvailablePercentage =
      ((this.booking?.filter(
        (booking) => booking.status.toLocaleLowerCase() !== 'CANCELLED'.toLocaleLowerCase()
      ).length ?? 0) /
        this.capacity) *
      100

    const rounded =
      capacityAvailablePercentage % 1 >= 0.5
        ? Math.ceil(capacityAvailablePercentage)
        : Math.floor(capacityAvailablePercentage)

    if (rounded >= 100) {
      return 'full'
    } else if (rounded >= 75) {
      return 'almost-full'
    } else {
      return 'open'
    }
  }
}

@Serializable()
export class SwimmingMemberSchedule extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('userId')
  userId!: number

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string

  @JsonProperty()
  email!: string

  @JsonProperty()
  phone!: string

  @JsonProperty('status')
  status!: string

  @DayjsProperty()
  bookedAt!: Dayjs

  @JsonProperty('cancelledAt')
  cancelledAt!: string | null

  public getStatus() {
    if (this.status.toLowerCase() === 'booked') {
      return 'Booked'
    } else if (this.status.toLowerCase() === 'cancelled') {
      return 'Cancelled'
    } else if (this.status.toLowerCase() === 'present') {
      return 'Joined'
    } else if (this.status.toLowerCase() === 'absent') {
      return 'Not Participating'
    }
    return this.status
  }
}

@Serializable()
export class SwimmingBookingResponse extends Fromable {
  @JsonProperty()
  id!: number

  @JsonProperty()
  userId!: number

  @JsonProperty()
  scheduleId!: number

  @JsonProperty()
  status!: string

  @JsonProperty()
  bookedAt!: string

  @JsonProperty()
  cancelledAt!: string | null

  @JsonProperty()
  userMembershipId!: number | null

  @JsonProperty({ type: SwimmingSchedule })
  schedule?: SwimmingSchedule
}
